export interface ShortcutEntry {
  keys: string;
  label: string;
  description: string;
  category: 'navigation' | 'actions' | 'selection' | 'pagination';
}

// --- Composable shortcut groups ---

export const BASE_SHORTCUTS: ShortcutEntry[] = [
  // Navigation
  {
    keys: 'ArrowDown',
    label: '↓ / J',
    description: 'Move focus to next row',
    category: 'navigation',
  },
  {
    keys: 'ArrowUp',
    label: '↑ / K',
    description: 'Move focus to previous row',
    category: 'navigation',
  },
  {
    keys: 'Home',
    label: 'Home',
    description: 'Focus first row',
    category: 'navigation',
  },
  {
    keys: 'End',
    label: 'End',
    description: 'Focus last row',
    category: 'navigation',
  },
  // Actions
  {
    keys: 'ArrowRight',
    label: '→',
    description: 'Expand focused row',
    category: 'actions',
  },
  {
    keys: 'ArrowLeft',
    label: '←',
    description: 'Collapse focused row',
    category: 'actions',
  },
  {
    keys: 'Enter',
    label: 'Enter',
    description: 'Toggle expand/collapse focused row',
    category: 'actions',
  },
];

export const ESCAPE_WITH_DESELECT: ShortcutEntry = {
  keys: 'Escape',
  label: 'Esc',
  description: 'Close expanded row; if none, deselect all',
  category: 'actions',
};

export const ESCAPE_COLLAPSE_ONLY: ShortcutEntry = {
  keys: 'Escape',
  label: 'Esc',
  description: 'Close expanded row',
  category: 'actions',
};

export const SELECTION_SHORTCUTS: ShortcutEntry[] = [
  {
    keys: 'space',
    label: 'Space',
    description: 'Toggle checkbox of focused row',
    category: 'selection',
  },
  {
    keys: 'mod+a',
    label: 'Ctrl/Cmd+A',
    description: 'Select all visible rows',
    category: 'selection',
  },
];

export const PAGINATION_SHORTCUTS: ShortcutEntry[] = [
  {
    keys: 'PageDown',
    label: 'PgDn',
    description: 'Go to next pagination page',
    category: 'pagination',
  },
  {
    keys: 'PageUp',
    label: 'PgUp',
    description: 'Go to previous pagination page',
    category: 'pagination',
  },
];

export const HELP_SHORTCUT: ShortcutEntry[] = [
  {
    keys: 'shift+/',
    label: '?',
    description: 'Open keyboard shortcuts help',
    category: 'actions',
  },
];

export const THREAT_TABLE_SHORTCUTS: ShortcutEntry[] = [
  {
    keys: 's',
    label: 'S',
    description: 'Open status dropdown on focused row',
    category: 'actions',
  },
  {
    keys: 'mod+s',
    label: 'Ctrl/Cmd+S',
    description: 'Bulk change status of selected rows',
    category: 'actions',
  },
  {
    keys: 'c',
    label: 'C',
    description: 'Copy threat name of focused row',
    category: 'actions',
  },
  {
    keys: 'o',
    label: 'O',
    description: 'Open threat detail in new tab',
    category: 'actions',
  },
];

export const REVIEW_MODE_SHORTCUTS: ShortcutEntry[] = [
  {
    keys: 'p',
    label: 'P',
    description: 'Toggle the threat preview panel',
    category: 'actions',
  },
  {
    keys: 'y',
    label: 'Y',
    description: 'Confirm focused threat',
    category: 'actions',
  },
  {
    keys: 'd',
    label: 'D',
    description: 'Discard focused threat',
    category: 'actions',
  },
  {
    keys: 'b',
    label: 'B',
    description: 'Mark focused threat as benign',
    category: 'actions',
  },
  {
    // 'm' (not 't') — 't' is reserved for takedown initiation in the threat
    // cockpit; keeping trademark off 't' avoids cross-surface muscle-memory
    // accidents.
    keys: 'm',
    label: 'M',
    description: 'Mark focused threat as trademark',
    category: 'actions',
  },
  {
    keys: 'w',
    label: 'W',
    description: 'Mark focused threat as owned',
    category: 'actions',
  },
];

export const FN_DISCOVERY_SHORTCUTS: ShortcutEntry[] = [
  {
    keys: 'p',
    label: 'P',
    description: 'Toggle the candidate preview panel',
    category: 'actions',
  },
  {
    keys: 'c',
    label: 'C',
    description: 'Copy domain of focused row',
    category: 'actions',
  },
  {
    keys: 't',
    label: 'T',
    description: 'Create threat from selected candidate (single selection)',
    category: 'actions',
  },
  {
    keys: 'd',
    label: 'D',
    description: 'Dismiss focused or all selected candidates',
    category: 'actions',
  },
];

// Full list for backward compatibility
export const SHORTCUT_MAP: ShortcutEntry[] = [
  ...BASE_SHORTCUTS,
  ESCAPE_WITH_DESELECT,
  ...THREAT_TABLE_SHORTCUTS,
  ...REVIEW_MODE_SHORTCUTS,
  ...HELP_SHORTCUT,
  ...SELECTION_SHORTCUTS,
  ...PAGINATION_SHORTCUTS,
];

export const getShortcutsForRoute = (pathname: string): ShortcutEntry[] => {
  if (pathname.startsWith('/fn-discovery')) {
    return [
      ...BASE_SHORTCUTS,
      ESCAPE_WITH_DESELECT,
      ...FN_DISCOVERY_SHORTCUTS,
      ...SELECTION_SHORTCUTS,
      ...PAGINATION_SHORTCUTS,
      ...HELP_SHORTCUT,
    ];
  }
  if (pathname.startsWith('/takedown')) {
    return [...BASE_SHORTCUTS, ESCAPE_COLLAPSE_ONLY, ...HELP_SHORTCUT];
  }
  // Home / dashboard — full threat table shortcuts
  return SHORTCUT_MAP;
};
